import { useContext, useEffect, useState } from "react";
import { CiMap, CiViewTable } from "react-icons/ci";
import { QrCodesV1Service } from "../../../common/services/api/api";
import { IQrCode, IQrCodeCreate } from "../models";
import { Loading } from "../../../common/components/loading/loading";
import Context from "../../../common/components/utils/context";
import { notifcation } from "../../../common/components/utils/notifications";
import { Themes } from "../../../common/enums/themes";
import { CodeGenerator } from "./codes/generate";
import { MapView } from "./codes/map";
import { TableView } from "./codes/table";
import { CodeEdit } from "./codes/edit";

export const AdminQrCode = () => {
  const context = useContext(Context);
  const service = new QrCodesV1Service();

  const [loading, setLoading] = useState(true);
  const [update, setUpdate] = useState(true);
  const [view, setView] = useState(false);
  const [codes, setCodes] = useState<IQrCode[]>([]);

  const toggleView = () => setView(!view);

  useEffect(() => {
    update && fetch().then(() => setUpdate(false));
  }, [update]);

  const fetch = async () => {
    try {
      setLoading(true);
      const response = await service.getQrCodes();
      setCodes(response.data);
    } catch (error) {
      console.error("Error fetching Check-In Points:", error);
    } finally {
      setLoading(false);
    }
  };

  const generate = (item: IQrCode) => {
    context.setModal({
      show: true,
      component: <CodeGenerator item={item} edit={edit} onClose={closeModal} />,
      size: "sm",
      onClose: closeModal,
    });
  };

  const closeModal = () => {
    context.setModal({
      ...context.modal,
      show: false,
    });
  };

  const save = async (item: IQrCode) => {
    const code: IQrCodeCreate = {
      description: item.description,
      latitude: +item.latitude,
      longitude: +item.longitude,
      altitude: +item.altitude,
      heading: +item.heading,
    };
    try {
      const result = item.id
        ? await service.update(item.id, {
            ...code,
            latitude: code.latitude,
            longitude: code.longitude,
            altitude: code.altitude,
            heading: code.heading,
            id: item.id,
            created: item.created,
          })
        : await service.create({
            ...code,
            latitude: code.latitude,
            longitude: code.longitude,
            altitude: code.altitude,
            heading: code.heading,
            id: item.id,
            created: item.created,
          });
      generate(result.data);
      context.setAlert({
        message: result.data
          ? `Check-In Point ${item.id ? "updated" : "created"} successfully.`
          : "An error has occurred. Please try again or contact your administrator.",
        theme: result.data ? Themes.Success : Themes.Danger,
      });
    } catch (error) {
      console.error(error);
    }
    setUpdate(true);
  };

  const edit = (item?: IQrCode) => {
    const data = item ? item : ({} as IQrCode);
    context.setModal({
      show: true,
      component: <CodeEdit {...data} save={save} />,
      size: "sm",
      onClose: () => context.setModal({ ...context.modal, show: false }),
      outsideClickToClose: false,
    });
  };

  const remove = async (item: IQrCode) => {
    const confirm = await notifcation({
      title: "Confirm Delete",
      message: `Are your sure you want to delete ${item.description} ?`,
      cancel: "Cancel",
      confirm: "Delete",
    });
    if (confirm) {
      const response = await service.deleteQrCode(item.id);
      if (response.status === 204) {
        setUpdate(true);
        context.setAlert({
          message: "Check-In Point deleted successfully!",
          theme: Themes.Success,
        });
      } else {
        context.setAlert({
          message: "Failed to delete Check-In Point!",
          theme: Themes.Danger,
        });
      }
    }
  };

  return loading ? (
    <Loading />
  ) : (
    <div className="container mb-auto relative">
      <div className="flex flex-col space-y-5">
        <div className="columns-2">
          <h2 className="text-2xl font-medium">Check-In Points</h2>
          <div className="grid grid-flow-col justify-end space-x-4">
            <div className="flex justify-center">
              <div
                className={"grid grid-flow-col items-center justify-end px-3"}
              >
                <button
                  type="button"
                  onClick={() => edit()}
                  className="transition-all rounded-lg hover:text-white text-gray-800 text-sm tracking-wider font-medium outline-none bg-text-link px-10 py-2"
                >
                  Create New Check-In Point
                </button>
              </div>
              <div className="inline-flex items-center overflow-hidden rounded-lg border border-stroke dark:border-dark-3">
                <button
                  disabled={view}
                  className={`border-r px-3 py-2 last-of-type:border-r-0 ${
                    view
                      ? "bg-gray-100 shadow-inner cursor-default"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={toggleView}
                >
                  <CiMap size={20} />
                </button>
                <button
                  disabled={!view}
                  className={`border-r px-3 py-2 last-of-type:border-r-0 ${
                    !view
                      ? "bg-gray-100 shadow-inner cursor-default"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={toggleView}
                >
                  <CiViewTable size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
        {view ? (
          <MapView
            codes={codes}
            generate={generate}
            remove={remove}
            edit={edit}
          />
        ) : (
          <TableView
            codes={codes}
            generate={generate}
            remove={remove}
            edit={edit}
          />
        )}
        <div
          className={`grid grid-flow-col items-center ${
            !view && "justify-end"
          }`}
        ></div>
      </div>
    </div>
  );
};

export interface ICodesView {
  codes: IQrCode[];
  generate: (item: IQrCode) => void;
  edit: (item: IQrCode) => void;
  remove: (item: IQrCode) => void;
}
